/**
 * The knowledge explorer has one selection model shared by the file tree, the
 * document library, the symbol search, the graphs and their tabular
 * alternatives. Everything downstream ("Erklären", "Einprägen", the preview
 * and the detail panel) reads these helpers instead of sniffing shapes.
 *
 * @typedef {{
 *   kind: 'file'|'directory'|'symbol'|'document'|'node',
 *   id: string,
 *   name: string,
 *   path?: string,
 *   content?: string,
 *   symbolKind?: string,
 *   file?: string,
 *   source?: 'axiom'|'catalog',
 *   authority?: string,
 *   status?: string,
 *   chunkCount?: number,
 *   data?: object
 * }} KnowledgeSelection
 */

/** @returns {KnowledgeSelection} */
export function fileSelection({ path, name, content }) {
  return { kind: 'file', id: path, name: name || path.split('/').pop(), path, content };
}

/** @returns {KnowledgeSelection} */
export function directorySelection({ path, name }) {
  return { kind: 'directory', id: path, name: name || path.split('/').pop(), path };
}

/** @returns {KnowledgeSelection} */
export function symbolSelection(symbol) {
  return {
    kind: 'symbol',
    id: symbol.id || symbol.symbolId || symbol.name,
    name: symbol.name || symbol.label || symbol.id,
    symbolKind: symbol.kind,
    file: symbol.file || symbol.location?.file,
    data: symbol
  };
}

/** @returns {KnowledgeSelection} */
export function documentSelection(document) {
  return {
    kind: 'document',
    id: String(document.id),
    name: document.filename || document.title || String(document.id),
    source: document.source || 'axiom',
    authority: document.authority,
    status: document.status,
    chunkCount: document.chunkCount ?? document.chunk_count,
    data: document
  };
}

/**
 * A node from either graph (or from a row of its tabular alternative).
 * @returns {KnowledgeSelection}
 */
export function nodeSelection(node) {
  const data = node.data || node;
  return {
    kind: 'node',
    id: node.id || data.path || data.label,
    name: data.label || node.id,
    path: data.path || data.relativePath,
    file: data.fileName,
    symbolKind: data.kind,
    data
  };
}

/**
 * Stable reference string, matching the source-descriptor `ref` contract used
 * by chat provenance (WAVE1-INTERFACES §5): code → repo-relative path,
 * document → `axiom:<id>`, catalog → `catalog:<deck>`, ckb → `ckb:…`.
 *
 * @param {KnowledgeSelection|null} selection
 * @returns {string|null}
 */
export function selectionRef(selection) {
  if (!selection) return null;
  switch (selection.kind) {
    case 'file':
    case 'directory':
      return selection.path || null;
    case 'symbol':
      return selection.id?.startsWith('ckb:') ? selection.id : `ckb:${selection.id}`;
    case 'document':
      return selection.source === 'catalog' ? `catalog:${selection.id}` : `axiom:${selection.id}`;
    case 'node':
      return selection.path || (selection.id ? `ckb:${selection.id}` : null);
    default:
      return null;
  }
}

/**
 * Anything that can meaningfully be explained or etched. Directories are
 * included (an architecture question about a module is a real question);
 * an absent selection obviously is not.
 *
 * @param {KnowledgeSelection|null} selection
 */
export function isActionable(selection) {
  return Boolean(selection && selection.name);
}

/**
 * The question sent into chat when the user picks "Erklären".
 *
 * @param {KnowledgeSelection} selection
 * @param {(key: string, options?: object) => string} t
 */
export function explainPrompt(selection, t) {
  const name = selection.path || selection.name;
  switch (selection.kind) {
    case 'symbol':
      return t('knowledge.explainPromptSymbol', { name: selection.name });
    case 'document':
      return t('knowledge.explainPromptDocument', { name: selection.name });
    case 'directory':
    case 'node':
      return t('knowledge.explainPromptModule', { name });
    default:
      return t('knowledge.explainPromptFile', { name });
  }
}

/**
 * Payload handed to `<EtchPreview/>`. The preview owns the mission authoring,
 * the `POST /api/missions` call, the confirm-before-save rule and the undo —
 * this only describes *what* was selected and proposes a Kernaussage.
 *
 * Shape is the ratified EtchPreview `selection` contract; `origin` is a
 * sibling prop on the component, not part of this object.
 *
 * @param {KnowledgeSelection} selection
 * @param {{
 *   t: (key: string, options?: object) => string,
 *   workspacePath?: string|null,
 *   excerpt?: string|null
 * }} context
 */
export function etchSubject(selection, { t, workspacePath = null, excerpt = null } = {}) {
  const kind =
    selection.kind === 'document' ? 'document'
      : selection.kind === 'symbol' ? 'symbol'
        : selection.kind === 'node' ? 'graph'
          : 'code';

  const label = selection.path || selection.file || selection.name;
  const statementKey = {
    document: 'knowledge.etchStatementDocument',
    symbol: 'knowledge.etchStatementSymbol',
    graph: 'knowledge.etchStatementModule'
  }[kind] || 'knowledge.etchStatementFile';

  return {
    kind,
    title: selection.name,
    keyStatement: t ? t(statementKey, { name: selection.name, source: label }) : selection.name,
    learningGoal: undefined,
    sourceRef: selectionRef(selection),
    sourceLabel: label,
    excerpt: excerpt || (selection.content ? selection.content.slice(0, 1200) : null),
    workspacePath
  };
}
